"use client";
import { useStore } from "@tanstack/react-store";
import { format } from "date-fns";
import { CirclePlay, Clock, LoaderCircle } from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import type { z } from "zod/v4";
import { GameInfoSkeleton } from "@/components/game-skeleton";
import { MarkSixDraw } from "@/components/gameDraw/MarkSixDraw";
import { MinuteLottoDraw } from "@/components/gameDraw/MiunteLottoDraw";
import { NationwideDraw } from "@/components/gameDraw/NationwideDraw";
import { PC28Draw } from "@/components/gameDraw/PC28Draw";
import { Pk10Draw } from "@/components/gameDraw/Pk10Draw";
import { ThreeDiceDraw } from "@/components/gameDraw/ThreeDiceDraw";
import { GameIcon } from "@/components/images/game-icon";
import { Button } from "@/components/ui/button";
import type { GameSchema } from "@/lib/schema";
import { gameListStore } from "@/lib/store";
import live from "@/live.json";

export default function PublicGameInfo({
  id,
  type,
}: {
  id?: string;
  type?: string;
}) {
  const searchParams = useSearchParams();
  const gameType = type ?? searchParams.get("gameType");
  const roomId = id ?? searchParams.get("roomId");
  const info = useStore(gameListStore, (state) =>
    state?.data?.find((game) => game.roomId === roomId),
  );

  if (!info) {
    return <GameInfoSkeleton />;
  }

  return (
    <div className="flex h-40 w-full gap-4 p-4">
      <Link
        className="shrink-0"
        href={{
          pathname: "/result",
          query: {
            roomId,
            gameType,
          },
        }}
      >
        <GameIcon roomId={roomId} width={100} height={100} />
      </Link>
      <div className="flex flex-1 flex-col gap-4">
        <Title game={info} />

        <div className="flex justify-between gap-4">
          <GameResult game={info} />

          <Countdown game={info} />
        </div>
      </div>
    </div>
  );
}

function Title({ game }: { game: z.infer<typeof GameSchema> }) {
  const gotoGameVideo = (gameType: string, roomId: string) => {
    const pathname = "/gameVideo";
    return {
      pathname,
      query: {
        gameType: gameType,
        roomId: roomId,
      },
    };
  };

  return (
    <div className="w-full">
      <div className="flex w-full items-center gap-2">
        <Link
          href={{
            pathname: "/result",
            query: {
              roomId: game.roomId,
              gameType: game.gameType,
            },
          }}
          className="max-w-xs truncate font-bold text-2xl"
        >
          {game.roomName}
        </Link>
        {game.preGameId && (
          <div className="mr-1 flex gap-1">
            <p className="my-auto font-bold text-base text-muted-foreground xl:text-xl">
              第
            </p>
            <p className="my-auto text-base text-primary xl:text-xl">
              {game.preGameId}
            </p>
            <p className="my-auto font-bold text-base text-muted-foreground xl:text-xl">
              期开奖结果
            </p>
          </div>
        )}
        <p className="my-auto mr-1 text-base text-muted-foreground xl:text-xl">
          {format(game.preTime, "yyyy-MM-dd HH:mm:ss")}
        </p>

        <div className="flex">
          {live[game.roomId] && (
            <Button asChild>
              <Link href={gotoGameVideo(game.gameType, game.roomId)}>
                <CirclePlay /> 游戏直播
              </Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Countdown({ game }: { game: z.infer<typeof GameSchema> }) {
  const [time, setTime] = useState(0);

  useEffect(() => {
    if (game.state === 1 && game.time) {
      setTime(Math.floor((game.time - Date.now()) / 1000));
    }

    const interval = setInterval(() => {
      setTime((prevTime) => {
        if (prevTime <= 0) {
          return 0;
        }
        return Math.floor(prevTime - 1);
      });
    }, 1000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [game.state, game.time]);

  const hourContent = String(Math.floor(time / 60 / 60)).padStart(2, "0");
  const mintueContent = String(Math.floor(time / 60) % 60).padStart(2, "0");
  const secondContent = String(time % 60).padStart(2, "0");

  return (
    <div>
      {game.state === 1 && time > 0 ? (
        <div className="flex w-92 shrink-0 items-center gap-4 rounded-md bg-gradient-to-r from-blue-200 to-blue-50 p-2 text-sm">
          <Clock className="size-4" />
          <span>
            距下期：
            <span className="font-bold text-primary">{game.gameId}</span> 期开奖
          </span>
          <span className="w-20 font-bold text-primary">
            {hourContent}:{mintueContent}:{secondContent}
          </span>
        </div>
      ) : [1, 2].includes(game.state) ? (
        <div className="flex items-center gap-2 rounded-md bg-gradient-to-r from-red-200 to-red-50 p-2 text-sm">
          <span className="text-destructive">正在开奖中</span>
          <LoaderCircle className="size-4 animate-spin opacity-50" />
        </div>
      ) : null}
    </div>
  );
}
function GameResult({ game }: { game: z.infer<typeof GameSchema> }) {
  if (!game.result) {
    return <div />;
  }

  const gameComponents = {
    "1070": <MarkSixDraw gameData={game} />, // 六合彩
    "1062": <Pk10Draw gameData={game} />, // PK10
    "1063": <MinuteLottoDraw gameData={game} />, // 时时彩
    "1068": <ThreeDiceDraw gameData={game} />, // 快三
    "10010": <NationwideDraw result={game.result} roomId={game.roomId} />, // 全国低频
    "10011": <NationwideDraw result={game.result} roomId={game.roomId} />, // 全国低频-体彩
    "10012": <NationwideDraw result={game.result} roomId={game.roomId} />, // 全国低频
    "1069": <PC28Draw result={game.result} />, // PC28
  };
  return <div>{gameComponents[game.gameType]}</div>;
}
